/* eslint-disable max-statements */
import React, { useContext, useEffect } from 'react';
import { Navigate, useParams, useLocation } from 'react-router-dom';
import ApplicationContext from 'components/Application/Context';
import { getDefaultLocation } from 'utils/auth';
import { join } from 'utils/history';
import {
  redirectTo,
  hasInternalAppAccess,
  hasBrandAppAccess,
} from 'store/authentication/utils';
import { Permissions, User } from 'components/User/types';
import storage from 'utils/storage';
import useUser from 'store/authentication/useUser';
import { PostHog } from 'posthog-js';
import { AmplitudeExperiments } from 'shared/types/user';
import { isImpersonating } from 'utils/impersonationSession';
import { formatUserId } from 'components/PostHog/utils';

import AbsoluteRedirect from '../Redirect/AbsoluteRedirect';
import { RoutePrivateComponentProps, RoutePrivateProps } from './types';

/* eslint-disable-next-line */
function getDefaultRoute(
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  { location, match, ...props }: any,
  user: User | null,
  token: string | null,
  permissions: Permissions | null,
  postHog: PostHog | null,
  logout: () => void,
  Component:
    | ((props: RoutePrivateComponentProps) => JSX.Element | null)
    | undefined,
  externalContext: boolean,
  grantAccess: ((permissions: Permissions) => boolean) | undefined,
  requiredPermission: string | undefined,
  amplitudeExperiments?: AmplitudeExperiments,
  requiredExperiment?: string,
) {
  if (!location) {
    return null;
  }

  // Not authenticated properly - BUT allow if we're impersonating
  // During impersonation, there's a brief moment where the token might not be set yet
  const currentlyImpersonating = isImpersonating();

  // Authentication check for impersonation scenarios
  // During impersonation, token might be briefly missing during state transitions

  if (!user || (!token && !currentlyImpersonating) || !permissions) {
    // Log authentication failures for debugging impersonation issues

    // If we're impersonating but missing token, it's likely a timing issue
    // Don't redirect immediately - the token should arrive soon
    if (currentlyImpersonating && !token) {
      // Show a loading state while waiting for token during impersonation transitions
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
          }}
        >
          <div>Loading impersonation session...</div>
        </div>
      );
    }

    if (externalContext) {
      storage.set(
        '$BEN$--externalRedirectOnAuthenticated',
        `${location?.pathname}${location?.search ?? ''}`,
      );
    } else {
      storage.set(
        '$BEN$--redirectOnAuthenticated',
        `${location?.pathname}${location?.search ?? ''}`,
      );
    }

    return (
      <Navigate
        to="/user/login"
        state={{
          from: location,
          redirectOnAuthenticated: true,
        }}
      />
    );
  }

  // User object, but no access
  // IMPORTANT: Don't logout here - it's handled in useEffect to prevent render-time state updates
  // When impersonating, we allow the navigation but don't logout
  const hasInternalAccess = hasInternalAppAccess(user);
  const hasBrandAccess = hasBrandAppAccess(user);
  const hasNoAccess = !hasBrandAccess && !hasInternalAccess;

  // Access control check - external users need special handling during impersonation

  if (hasNoAccess && !currentlyImpersonating) {
    // Redirect to login for users with no access (but not during impersonation)
    return <Navigate to="/user/login" />;
  }

  // If impersonating a user with no access, let redirectTo() handle the routing
  // instead of forcing a login redirect which causes the loop
  if (hasNoAccess && currentlyImpersonating) {
    // During impersonation, let the component render instead of forcing login redirect
    // This prevents redirect loops for external users with no app access
  }

  const defaultLocation = getDefaultLocation(user);

  // check if user has product placement team
  if (user) {
    const hasProductPlacementTeam =
      user.team?.includes('product placement') ||
      user.team?.includes('traditional');

    const isExternal = user.role === 'external';

    const bypassPPTeamCheck =
      postHog?.isFeatureEnabled('allowInfUserEntryOnPPApp') || false;
    // redirect to /not-pp-team
    // if user is not on product placement team and feature flag is not enabled
    // we wait for posthog to load before checking if we should redirect, as it can take a while
    if (
      postHog &&
      location.pathname !== '/not-pp-team' &&
      !hasProductPlacementTeam &&
      !bypassPPTeamCheck &&
      !isExternal
    ) {
      postHog?.capture('pp:redirect-inf-to-right-platform', {
        userId: user?.id && formatUserId(user?.id),
        pageAttemptUrl: defaultLocation,
      });
      return <Navigate to="/not-pp-team" replace />;
    }
  }

  // Don't apply app routing restrictions during impersonation
  // This prevents redirect loops for external users with no access
  const redirectLocation = !currentlyImpersonating
    ? redirectTo({
        defaultLocation,
        grantAccess,
        permissions,
        requiredPermission,
        token,
        user,
        externalContext,
        amplitudeExperiments,
        requiredExperiment,
      })
    : null;

  if (redirectLocation && redirectLocation.pathname !== location.pathname) {
    const { replace, ...to } = redirectLocation;
    const isOnBrandApp = externalContext;
    const isOnInternalApp = externalContext === false;

    // Access control applies to all users, including impersonated ones
    if (isOnInternalApp && !hasInternalAppAccess(user)) {
      // User doesn't have internal app access but IS authenticated
      // Show showcase-only access page (they stay logged in)
      return <Navigate to="/user/showcase-only-access" />;
    }

    if (isOnBrandApp && !hasBrandAppAccess(user)) {
      // Redirect internal users to internal app
      return (
        <AbsoluteRedirect to={join('/team/', to.pathname)} replace={replace} />
      );
    }

    return <Navigate to={to} />;
  }

  if (Component) {
    return (
      <Component
        {...props}
        permissions={permissions}
        user={user}
        location={location}
        match={match}
      />
    );
  }

  return null;
}

export default function RoutePrivate({
  grantAccess,
  requiredPermission,
  element: Component,
  render,
  externalContext = false,
  requiredExperiment,
}: RoutePrivateProps) {
  const { user: authenticatedUser } = useUser();
  const amplitudeExperiments = React.useMemo(
    () => authenticatedUser?.amplitudeExperiments ?? {},
    [authenticatedUser?.amplitudeExperiments],
  );

  const { match, ...props } = useParams();
  const location = useLocation();
  const {
    token,
    user,
    permissions,
    permissionsIsLoading,
    logout,
    postHog,
  } = useContext(ApplicationContext);

  // Track impersonation state for routing decisions
  const currentlyImpersonating = isImpersonating();

  // Handle logout in useEffect to prevent render-time state updates
  const shouldLogout = React.useMemo(() => {
    // Only logout if user exists but has no access AND we're not impersonating
    const hasNoAccess =
      user && !hasBrandAppAccess(user) && !hasInternalAppAccess(user);

    return hasNoAccess && !currentlyImpersonating;
  }, [user, currentlyImpersonating]);

  useEffect(() => {
    if (shouldLogout) {
      logout();
    }
  }, [shouldLogout, logout]);

  if (!render && !Component) {
    return null;
  }
  if (permissionsIsLoading) {
    return null;
  }

  return getDefaultRoute(
    { location, match, ...props },
    user,
    token,
    permissions,
    postHog,
    logout,
    Component ?? undefined,
    externalContext,
    grantAccess ?? undefined,
    requiredPermission ?? undefined,
    amplitudeExperiments,
    requiredExperiment,
  );
}

RoutePrivate.defaultProps = {
  component: null,
  externalContext: false,
  grantAccess: null,
  render: null,
  requiredPermission: null,
};
